import {
  ComponentPropsWithoutRef,
  forwardRef,
  ReactElement,
  useId,
  useMemo,
} from "react";
import { stripInlineMarkdown, warnOnce } from "../../internal/functions";
import styles from "./Radio.module.scss";
import cx from "classnames";
import { LayoutUtilProps } from "../../types";
import { useLayoutPropsUtil } from "../../internal/hooks";
import { FieldLabel } from "../FieldLabel";
import type { FieldLabelProps as FieldLabelElementProps } from "../FieldLabel";
import { FieldLabelProps as InternalFieldLabelProps } from "../../internal/types";
import { HelperProps } from "../../internal/components";
import FieldMessage, { warnDeprecatedErrorUsage } from "../FieldMessage";
import { RadioGroupContext } from "./internal/RadioGroupContext";

/**
 * Props for the RadioGroup component
 * @extends ComponentPropsWithoutRef<"fieldset">
 * @extends LayoutUtilProps
 * @extends Omit<InternalFieldLabelProps, "labelProps">
 */
export type RadioGroupProps = ComponentPropsWithoutRef<"fieldset"> &
  LayoutUtilProps &
  Omit<InternalFieldLabelProps, "labelProps"> & {
    /**
     * Required flag for label
     * @remarks This does not enforce error when nothing is selected
     */
    required?: boolean;

    // TODO(v4.0.0): type becomes string only
    /**
     * Legend for the fieldset.
     * Passing `ReactNode` is deprecated — use a plain string with inline markdown instead.
     */
    legend: string | ReactElement;

    /**
     * Additional props for the legend `FieldLabel` (for example `aiMark`, `className`).
     */
    legendProps?: Omit<FieldLabelElementProps, "el">;

    /**
     * When `true`, hides the visual label row. The sr-only `<legend>` always remains for AT.
     * @default false
     */
    hideLabel?: boolean;

    /**
     * Error state for the field. Pass `true` to indicate error styling without a message.
     * Pass a string, string[], or ReactElement (deprecated) for error messages.
     */
    error?: boolean | string | ReactElement | string[];

    /**
     * @deprecated No longer used. Error messages always use `aria-live="assertive"`.
     */
    errorAriaLive?: HelperProps["errorAriaLive"];

    /**
     * Warning message(s) to display. Supports a single string or an array of strings.
     */
    warning?: string | string[];

    /**
     * Description text to display below the field.
     */
    description?: HelperProps["description"];

    /**
     * Hint text to display below the field.
     */
    hint?: HelperProps["hint"];
  };

/**
 * RadioGroup component for grouping related radio options.
 *
 * Features:
 * - Groups multiple radio options together
 * - Legend support for group labeling
 * - Required field indication
 * - Accessibility support with proper fieldset semantics
 * - Layout utility props for positioning and spacing
 * - Focus and blur event handling
 * - More info support for additional context
 * - Automatic alignment based on flex direction
 *
 * @example
 * <RadioGroup legend="Choose your preference" required>
 *   <Radio label="Option A" value="a" />
 *   <Radio label="Option B" value="b" />
 *   <Radio label="Option C" value="c" />
 * </RadioGroup>
 *
 * @example
 * <RadioGroup
 *   legend="Horizontal layout"
 *   flexDirection="row"
 *   gap="4"
 * >
 *   <Radio label="Yes" value="yes" />
 *   <Radio label="No" value="no" />
 * </RadioGroup>
 */
export const RadioGroup = forwardRef<HTMLFieldSetElement, RadioGroupProps>(
  (props, ref) => {
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const {
      className,
      children,
      description,
      error,
      errorAriaLive: _errorAriaLive,
      hint,
      warning,
      legend,
      moreInfo,
      legendProps,
      onBlur,
      onFocus,
      required,
      style,
      hideLabel = false,
      ...rest
    } = componentProps;

    const RadioGroupClassNames = cx(styles["radio-group"], className);
    const helperUid = useId();
    const moreInfoUid = useId();

    const styleCombined = {
      ...style,
      ...layoutStyles,
      alignItems:
        layoutStyles?.alignItems ??
        (layoutStyles?.flexDirection === "row" ? "center" : undefined),
    };

    warnDeprecatedErrorUsage("RadioGroup", error);

    // TODO(v4.0.0): remove once legend is string only
    if (typeof legend !== "string") {
      warnOnce(
        "RadioGroup: Passing ReactNode to legend is deprecated. Use a plain string with inline markdown instead.",
      );
    }

    const errorMessages =
      typeof error === "boolean" || error === undefined ? undefined : error;

    const hasHelperText = hint || errorMessages || warning || description;

    const ariaDescribedBy =
      [hasHelperText && helperUid, moreInfo && moreInfoUid]
        .filter(Boolean)
        .join(" ") || undefined;

    const contextValue = useMemo(
      () => ({
        hasError: !!error,
      }),
      [error],
    );

    return (
      <fieldset
        role="radiogroup"
        data-anv="radio-group"
        className={RadioGroupClassNames}
        aria-required={required}
        style={styleCombined}
        ref={ref}
        onFocus={onFocus}
        onBlur={onBlur}
        {...rest}
        aria-describedby={ariaDescribedBy}
      >
        <legend className={styles["legend"]}>
          {/* TODO(v4.0.0): remove typeof check — legend will always be string */}
          {typeof legend === "string" ? stripInlineMarkdown(legend) : legend}
          {required && ", required"}
        </legend>
        {!hideLabel && (
          <div className={styles["label-row"]}>
            <FieldLabel
              aria-hidden
              required={required}
              moreInfo={moreInfo}
              moreInfoId={moreInfo ? moreInfoUid : undefined}
              {...legendProps}
              className={legendProps?.className}
            >
              {legend}
            </FieldLabel>
          </div>
        )}
        <RadioGroupContext.Provider value={contextValue}>
          <div
            className={styles["children"]}
            style={{
              flexDirection: layoutStyles?.flexDirection,
              gap: layoutStyles?.gap,
            }}
          >
            {children}
          </div>
        </RadioGroupContext.Provider>
        {hasHelperText ? (
          <FieldMessage
            id={helperUid}
            hint={hint}
            error={errorMessages}
            warning={warning}
            description={description}
          />
        ) : null}
      </fieldset>
    );
  },
);

RadioGroup.displayName = "RadioGroup";
